/**
 * <video-hero>
 *
 * Drives the background video for the Video hero section.
 *
 * The section renders a single <video> with no `src`. This element picks the
 * correct source for the current viewport and assigns it, which guarantees that
 * exactly one video file is ever downloaded. The previous implementation put a
 * desktop and a mobile <video autoplay> in the DOM and hid one with CSS, so both
 * files were fetched on the theme's largest, most performance-critical page.
 *
 * Under `prefers-reduced-motion: reduce` no source is assigned at all, so the
 * video is never fetched and the poster image is what the visitor sees. Without
 * JavaScript the same is true.
 *
 * Being a custom element rather than an inline <script> means it upgrades once
 * per instance — two heroes on one page no longer bind duplicate listeners —
 * and it re-initialises correctly when the theme editor re-renders the section.
 */
class VideoHero extends HTMLElement {
  connectedCallback() {
    this.video = this.querySelector('[data-video-hero-video]');
    this.toggle = this.querySelector('[data-video-hero-toggle]');

    if (!this.video) return;

    this.mobileQuery = window.matchMedia(`(max-width: ${this.dataset.mobileBreakpoint || '749'}px)`);

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.onSourceChange = this.onSourceChange.bind(this);
    this.mobileQuery.addEventListener('change', this.onSourceChange);

    this.video.addEventListener('playing', () => this.classList.add('video-hero--ready'), { once: true });

    if (this.toggle) {
      this.toggle.hidden = false;
      this.toggle.addEventListener('click', this.onToggleClick.bind(this));
    }

    this.loadSource();
  }

  disconnectedCallback() {
    this.mobileQuery?.removeEventListener('change', this.onSourceChange);
  }

  get sourceUrl() {
    const { videoMobile, videoDesktop } = this.dataset;
    if (this.mobileQuery.matches && videoMobile) return videoMobile;
    return videoDesktop || videoMobile;
  }

  loadSource() {
    const url = this.sourceUrl;
    if (!url || this.video.getAttribute('src') === url) return;

    this.video.setAttribute('src', url);
    this.video.load();
    this.play();
  }

  onSourceChange() {
    // Crossing the breakpoint only matters when the two sources actually differ;
    // otherwise a resize would re-download an identical file for no reason.
    if (this.dataset.videoMobile === this.dataset.videoDesktop) return;
    this.loadSource();
  }

  onToggleClick() {
    const wasPaused = this.video.paused;

    if (wasPaused) {
      this.play();
    } else {
      this.video.pause();
    }

    this.classList.toggle('video-hero--paused', !wasPaused);
    this.toggle.setAttribute('aria-pressed', String(wasPaused));
  }

  play() {
    // Autoplay can be refused (low power mode, browser policy). The poster stays
    // visible in that case, so a rejected promise is not an error worth logging.
    this.video.play()?.catch(() => {});
  }
}

if (!customElements.get('video-hero')) {
  customElements.define('video-hero', VideoHero);
}
